import React, { useRef } from "react";
import { Send, Upload, FileText, X } from "lucide-react";

interface ChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  uploadedFile: File | null;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  loading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  input,
  onInputChange,
  onSend,
  uploadedFile,
  onFileUpload,
  onRemoveFile,
  loading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className="px-4 py-3 sm:px-6 sm:py-4 border-t"
      style={{
        borderColor: "var(--border-color)",
        backgroundColor: "var(--card-bg)",
      }}
    >
      {uploadedFile && (
        <div
          className="mb-2 px-3 py-2 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div className="flex items-center gap-2">
            <FileText
              className="w-4 h-4"
              style={{ color: "var(--primary-color)" }}
            />
            <span
              className="text-xs sm:text-sm"
              style={{ color: "var(--primary-color)" }}
            >
              {uploadedFile.name}
            </span>
          </div>
          <button
            onClick={onRemoveFile}
            className="hover:opacity-70 transition-opacity"
            style={{ color: "var(--primary-color)" }}
            title="Remover arquivo"
            aria-label="Remover arquivo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="file"
          ref={fileInputRef}
          accept=".pdf"
          onChange={onFileUpload}
          className="hidden"
          aria-label="Enviar arquivo PDF"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2.5 sm:p-3 rounded-xl border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 shrink-0"
          title="Enviar PDF"
          disabled={loading}
          style={{ borderColor: "var(--border-color)" }}
        >
          <Upload
            className="w-5 h-5"
            style={{ color: "var(--text-secondary)" }}
          />
        </button>

        <input
          type="text"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && !loading && onSend()}
          placeholder="Pergunte ao SIGA..."
          className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 focus:outline-none disabled:opacity-50 transition-colors text-sm sm:text-base"
          style={{
            borderColor: "var(--border-color)",
            backgroundColor: "var(--input-bg)",
            color: "var(--text-primary)",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "var(--primary-color)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--border-color)";
          }}
          disabled={loading}
        />

        <button
          onClick={onSend}
          disabled={(!input.trim() && !uploadedFile) || loading}
          className="px-4 sm:px-6 py-2.5 sm:py-3 text-white rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color)" }}
          title="Enviar mensagem"
        >
          <Send className="w-5 h-5" />
          <span className="hidden sm:inline">Enviar</span>
        </button>
      </div>
    </div>
  );
};
